/**
 * Catchball Store
 *
 * Zustand store for the catchball proposal workflow. Originally Workstream 6
 * ("Workflow Enforcement"), this store's Gen-1 in-memory write path was retired
 * under DEPRECATION_REGISTER DEP-023: it now survives as a read + notify surface
 * plus RPC-backed proposal resolution actions.
 *  - Reads: fetchFromSupabase / fetchConfigsFromSupabase / fetchThreadsFromSupabase,
 *    the getProposal / getStats / getPendingActions selectors, and the loop-result
 *    read paths (fetchLoopHistory / fetchCycleLoopResults).
 *  - Notify: notifyProposalEvent is the home for proposal-lifecycle notifications,
 *    fired by the RPC-backed formProposal/submitProposal/acceptProposal/
 *    acceptProposalWithConditions/returnProposal/rejectProposal actions.
 *  - Retired: the in-memory proposeDown/proposeUp/createProposal/createLoopProposal/
 *    createItemProposal/counter/accept/reject/withdraw/resolveThread/setCycleConfig
 *    mutations (fake local governance state that never persisted). See DEP-023.
 *    The dashboard's "Needs your response" list now opens the proposal's review
 *    page, where the decision is made through catchball_accept / catchball_reject.
 * The RPC-backed proposalPacketStore + the catchball_* RPC family are the
 * canonical governance write paths.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import type {
  CatchballProposal,
  CatchballCounter,
  CatchballThread,
  CatchballStats,
  CatchballConfig,
  CatchballProposalStatus,
  CatchballFilters,
  PendingCatchballAction,
} from '../../types/prioritization/catchball';
import { calculateDaysPending, isProposalOverdue } from '../../types/prioritization/catchball';
import {
  getSafeSupabaseClient,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { sendNotification } from '../../lib/notificationService';
import { findCompetingBothChosen } from '../../lib/itemRelationshipFlags';
import { useSessionStore } from './sessionStore';
import { useItemRelationshipsStore } from './itemRelationshipsStore';
import { useItemsStore } from './itemsStore';
import {
  acceptCatchballProposal,
  canDecideProposal,
  draftCatchballProposal,
  rejectCatchballProposal,
  returnCatchballProposal,
  submitCatchballProposal,
} from '../../lib/catchball/proposalDecisions';
const TABLE_NAME = 'catchball_proposals';
const CONFIGS_TABLE = 'catchball_configs';
const THREADS_TABLE = 'catchball_threads';
const LOOP_RESULTS_TABLE = 'loop_results';

// ─── Proposal lifecycle notifications (brief-X §5 / GAP-4) ───────────────────
//
// Server triggers own telling the people directly involved:
//   * 737 trg_notify_catchball_proposal_resolved (AFTER UPDATE OF status) tells
//     `proposed_by` on accepted / accepted_provisional / returned / rejected —
//     every decision now runs through catchball_accept / catchball_reject /
//     catchball_return_for_revision (763), which write that status column.
//   * 763 trg_notify_catchball_proposal_submitted tells the evaluator side
//     (target recipients + the loop's requester) when a proposal is sent up,
//     so the client 'submitted' send is gone.
// What stays here: the accepted/rejected pings to the session facilitators /
// participants on the cycle so contributors see the loop close — an audience
// neither trigger reaches.
type ProposalNotifyKind = 'accepted' | 'rejected';

/**
 * Fire-and-forget notification for proposal lifecycle moments the server
 * triggers do not cover: 'accepted' / 'rejected' notify only the session
 * facilitators/participants on the cycle (an audience beyond `proposed_by`,
 * which the 737 trigger already handles) so contributors see the loop close.
 * Never blocks or fails the action.
 */
function notifyProposalEvent(proposalId: string, actorId: string, kind: ProposalNotifyKind): void {
  void (async () => {
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      const { data: proposal } = await supabase
        .from(TABLE_NAME)
        .select('loop_id, cycle_id, proposed_by, packaged_by')
        .eq('id', proposalId)
        .maybeSingle();
      if (!proposal) return;

      // proposed_by / packaged_by are deliberately excluded here — the 737
      // trigger (trg_notify_catchball_proposal_resolved) already notifies
      // that proposer directly on this same status transition. Only the
      // session people it does not reach are pinged below.
      let recipientIds: string[] = [];
      if (proposal.cycle_id) {
        const { data: sessions } = await supabase
          .from('prioritization_sessions')
          .select('id, facilitator_id')
          .eq('cycle_id', proposal.cycle_id);
        const sessionIds = (sessions ?? []).map((s: { id: string }) => s.id);
        for (const s of sessions ?? []) {
          if (s.facilitator_id) recipientIds.push(s.facilitator_id as string);
        }
        if (sessionIds.length > 0) {
          const { data: parts } = await supabase
            .from('session_participants')
            .select('user_id')
            .in('session_id', sessionIds);
          for (const p of parts ?? []) {
            if (p.user_id) recipientIds.push(p.user_id as string);
          }
        }
      }
      recipientIds = Array.from(new Set(recipientIds)).filter(id => id !== actorId);
      if (recipientIds.length === 0) return;

      // Catchball proposal ids live in catchball_proposals — review at
      // /catchball/proposal/:id. Never /governance/proposals/:id (that table is
      // priority_proposals; same UUID → "Proposal not found").
      const catchballProposalUrl = `/prioritization/cascade/proposal/${proposalId}`;
      const meta: Record<ProposalNotifyKind, { event: 'catchball_proposal_accepted' | 'catchball_proposal_rejected'; title: string; body: string; actionUrl: string; actionLabel: string }> = {
        accepted: {
          event: 'catchball_proposal_accepted',
          title: 'A proposal was accepted — priorities ratified',
          body: 'The ranking sent up is now the Published Priority List.',
          actionUrl: `/prioritization/cycles/${proposal.cycle_id}/report`,
          actionLabel: 'See the plan',
        },
        rejected: {
          event: 'catchball_proposal_rejected',
          title: 'A proposal was rejected',
          body: 'The evaluator closed this loop without ratification. See their rationale.',
          actionUrl: catchballProposalUrl,
          actionLabel: 'See why',
        },
      };
      const m = meta[kind];
      await sendNotification({
        event: m.event,
        channels: ['in_app', 'email'],
        recipientIds,
        senderId: actorId,
        title: m.title,
        body: m.body,
        priority: 'high',
        actionUrl: m.actionUrl,
        actionLabel: m.actionLabel,
        data: { proposalId, loopId: proposal.loop_id, cycleId: proposal.cycle_id },
      });
    } catch (err) {
      console.warn('[CatchballStore] Proposal notification failed (non-blocking):', err);
    }
  })();
}

// ─── LoopResult type (Brief D) ────────────────────────────────────────────────

export interface LoopResult {
  id: string;
  catchball_loop_id: string;
  cycle_id: string;
  workspace_id: string;
  turn_number: number;
  direction: 'up' | 'down';
  scope_track_ids: string[];
  proposal_id: string | null;
  session_result_ids: string[];
  official_track_result_id: string | null;
  item_pool_snapshot_id: string | null;
  rationale: string | null;
  tradeoffs: object | null;
  disagreements: object | null;
  assumptions: string | null;
  evaluator_decision: 'accepted' | 'accepted_provisional' | 'returned' | 'rejected' | null;
  evaluator_rationale: string | null;
  revision_guidance: string | null;
  submitted_by: string;
  submitted_at: string;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

interface CatchballState {
  // Data
  proposals: Record<string, CatchballProposal>;
  counters: Record<string, CatchballCounter>;
  threads: Record<string, CatchballThread>;
  configs: Record<string, CatchballConfig>;  // by cycleId
  
  // Loading state
  isLoading: boolean;
  error: string | null;
  
  // Supabase sync (read paths only)
  fetchFromSupabase: () => Promise<void>;
  fetchConfigsFromSupabase: () => Promise<void>;
  fetchThreadsFromSupabase: () => Promise<void>;

  // Loop-aware selectors
  getLoopProposals: (loopId: string) => CatchballProposal[];
  getItemProposals: (loopId: string) => CatchballProposal[];


  // Selectors
  getProposal: (id: string) => CatchballProposal | undefined;
  getProposalsForCycle: (cycleId: string) => CatchballProposal[];
  getProposalsForItem: (itemId: string) => CatchballProposal[];
  getProposalsByFilter: (filters: CatchballFilters) => CatchballProposal[];
  getPendingProposals: (cycleId: string) => CatchballProposal[];
  getPendingActions: (userId: string, cycleId?: string) => PendingCatchballAction[];
  getCountersForProposal: (proposalId: string) => CatchballCounter[];
  getThread: (threadId: string) => CatchballThread | undefined;
  getThreadsForCycle: (cycleId: string) => CatchballThread[];
  getStats: (cycleId: string) => CatchballStats;
  getConfig: (cycleId: string) => CatchballConfig | undefined;
  
  /**
   * Package a finished sitting's ranking into a draft proposal on its loop.
   *
   * §2.3a: one server transaction (catchball_draft_proposal, migration 772).
   * The server reads the ranking, the item pool and the session report, points
   * the loop at the draft, and refuses anyone who is not the ball holder, the
   * loop's creator/requester, the sitting's facilitator, or someone with
   * cycle edit rights. `cycleId` / `workspaceId` / `actorId` are no longer
   * sent — the server takes them from the loop row and auth.uid().
   *
   * Returns null on refusal; the plain-language reason is on `error`.
   */
  formProposal: (params: {
    sessionId: string;
    trackId: string;
    loopId: string;
    cycleId: string;
    workspaceId: string;
    actorId: string;
    actorName: string;
  }) => Promise<{ proposalId: string } | null>;
  /**
   * Stamp the packaged session onto a proposal and retire it from the loop's
   * pending-send set, in one transaction (mig 670). Called after a successful
   * submit: without it a sent session stays checked and every later pass
   * re-sends the same ranking.
   */
  recordProposalSession: (params: {
    proposalId: string;
    sessionId: string;
    actorId: string;
  }) => Promise<boolean>;
  submitProposal: (params: {
    proposalId: string;
    rationale: string;
    tradeoffs?: object[];
    disagreements?: object[];
    assumptions?: string;
    actorId: string;
    workspaceId: string;
  }) => Promise<boolean>;

  // Loop Results (Brief D)
  fetchLoopHistory: (loopId: string) => Promise<LoopResult[]>;
  fetchCycleLoopResults: (cycleId: string) => Promise<LoopResult[]>;

  // Executive Resolution Actions (Brief C)
  acceptProposal: (params: {
    proposalId: string;
    loopId: string;
    cycleId: string;
    rationale?: string;
    ratificationAuthority?: string;
    actorId: string;
    workspaceId: string;
  }) => Promise<boolean>;
  acceptProposalWithConditions: (params: {
    proposalId: string;
    loopId: string;
    cycleId: string;
    conditions: string;
    actorId: string;
    workspaceId: string;
  }) => Promise<boolean>;
  returnProposal: (params: {
    proposalId: string;
    loopId: string;
    cycleId: string;
    revisionGuidance: string;
    actorId: string;
    workspaceId: string;
  }) => Promise<boolean>;
  rejectProposal: (params: {
    proposalId: string;
    loopId: string;
    cycleId: string;
    rationale: string;
    actorId: string;
    workspaceId: string;
  }) => Promise<boolean>;

  // Utility
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialState = {
  proposals: {},
  counters: {},
  threads: {},
  configs: {},
  isLoading: false,
  error: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Brief D: loop_results writes happen inside the RPCs (migration 314).
// writeLoopResult() and resolveLoopResult() were removed — no client-side
// loop_results writes. The store only reads via fetchLoopHistory() /
// fetchCycleLoopResults().
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The sentence to show when a session's result has two competing items both
 * above its line, or null when it may go up. Reads the session and the
 * relationship graph from their stores, loading the graph if no page has.
 */
async function findCompetingChosenInSession(sessionId: string): Promise<string | null> {
  const session = useSessionStore.getState().sessions.find((s) => s.id === sessionId);
  const rankedItemIds = session?.rankingOutput?.rankedItemIds ?? [];
  if (!session || rankedItemIds.length === 0 || session.cutLinePosition == null) return null;

  const rel = useItemRelationshipsStore.getState();
  if (!rel.syncState.lastSyncAt) await rel.fetchFromSupabase().catch(() => {});
  const { groups, groupMemberships, edges } = useItemRelationshipsStore.getState();

  const sets = findCompetingBothChosen(
    { id: session.id, name: session.name, rankedItemIds, cutLinePosition: session.cutLinePosition },
    { groups, memberships: groupMemberships, edges },
  );
  if (sets.length === 0) return null;

  const items = useItemsStore.getState().items;
  const titleOf = (id: string) => items.find((i) => i.id === id)?.title ?? 'an item you cannot see';
  const names = sets.map((ids) => ids.map(titleOf).join(' and ')).join('; ');
  return `This result cannot go up yet. ${names} compete, and both are above the line. Choose one, then send it up.`;
}

export const useCatchballStore = create<CatchballState>()(
  (set, get) => ({
    ...initialState,
    
    // Supabase sync
    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[CatchballStore] Supabase not available');
        return;
      }
      
      set({ isLoading: true, error: null });
      
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        const { data, error } = await supabase
          .from(TABLE_NAME)
          .select('*')
          .order('proposed_at', { ascending: false });
        
        if (error) {
          console.error('[CatchballStore] Fetch error:', error);
          set({ isLoading: false, error: error.message });
          return;
        }
        
        if (data) {
          const proposalsMap: Record<string, CatchballProposal> = {};
          
          for (const row of data) {
            const proposal: CatchballProposal = {
              id: row.id,
              cycleId: row.cycle_id,
              proposalType: row.proposal_type || 'item',
              loopId: row.loop_id,
              focusListId: row.focus_list_id,
              itemId: row.item_id,
              sessionId: row.session_id ?? undefined,
              proposedBy: row.proposed_by,
              proposedByName: row.proposed_by_name,
              proposedByOrgUnit: row.proposed_by_org_unit || '',
              proposedByOrgUnitName: row.proposed_by_org_unit_name,
              proposedAt: new Date(row.proposed_at),
              direction: row.direction,
              targetOrgUnit: row.target_org_unit,
              targetOrgUnitName: row.target_org_unit_name,
              targetRecipients: row.target_recipients || [],
              proposedRank: row.proposed_rank,
              proposedCutLine: row.proposed_cut_line,
              proposedAction: row.proposed_action,
              rationale: row.rationale,
              status: row.status,
              respondedAt: row.responded_at ? new Date(row.responded_at) : undefined,
              respondedBy: row.responded_by,
              resolutionNote: row.response_note,
              counterProposalIds: row.counter_proposal_ids || [],
              threadId: row.thread_id,
              threadDepth: row.thread_depth || 0,
              parentProposalId: row.parent_proposal_id,
              responseDeadline: row.response_deadline ? new Date(row.response_deadline) : undefined,
            };
            proposalsMap[proposal.id] = proposal;
          }
          
          set({ proposals: proposalsMap, isLoading: false, error: null });
          console.log(`[CatchballStore] Loaded ${data.length} proposals from Supabase`);
          
          // Also fetch configs and threads
          await get().fetchConfigsFromSupabase();
          await get().fetchThreadsFromSupabase();
        }
      } catch (err) {
        console.error('[CatchballStore] Fetch exception:', err);
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
      }
    },
    
    // Fetch configs from Supabase
    fetchConfigsFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[CatchballStore] Supabase not available for configs');
        return;
      }
      
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        const { data, error } = await supabase
          .from(CONFIGS_TABLE)
          .select('*');
        
        if (error) {
          console.error('[CatchballStore] Fetch configs error:', error);
          return;
        }
        
        if (data) {
          const configsMap: Record<string, CatchballConfig> = {};
          
          for (const row of data) {
            const config: CatchballConfig = {
              cycleId: row.cycle_id,
              enabled: row.enabled ?? true,
              defaultResponseDeadlineDays: row.default_response_deadline_days ?? 3,
              orgUnitHierarchy: row.org_unit_hierarchy ?? [],
              requireRationale: row.require_rationale ?? true,
              allowMultipleCounters: row.allow_multiple_counters ?? true,
              maxThreadDepth: row.max_thread_depth ?? 5,
              notifyOnProposal: row.notify_on_proposal ?? true,
              notifyOnOverdue: row.notify_on_overdue ?? true,
              escalateAfterDays: row.escalate_after_days ?? 5,
            };
            configsMap[config.cycleId] = config;
          }
          
          set({ configs: configsMap });
          console.log(`[CatchballStore] Loaded ${data.length} configs from Supabase`);
        }
      } catch (err) {
        console.error('[CatchballStore] Fetch configs exception:', err);
      }
    },
    
    // Fetch threads from Supabase
    fetchThreadsFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[CatchballStore] Supabase not available for threads');
        return;
      }
      
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        const { data, error } = await supabase
          .from(THREADS_TABLE)
          .select('*')
          .order('started_at', { ascending: false });
        
        if (error) {
          console.error('[CatchballStore] Fetch threads error:', error);
          return;
        }
        
        if (data) {
          const threadsMap: Record<string, CatchballThread> = {};
          
          for (const row of data) {
            const thread: CatchballThread = {
              id: row.id,
              cycleId: row.cycle_id,
              itemId: row.item_id,
              itemTitle: row.item_title || '',
              startedAt: new Date(row.started_at),
              startedBy: row.started_by || '',
              proposalCount: row.proposal_count ?? 0,
              latestProposalAt: row.latest_proposal_at ? new Date(row.latest_proposal_at) : new Date(row.started_at),
              status: row.status || 'active',
              resolution: row.resolution,
              participants: row.participants ?? [],
            };
            threadsMap[thread.id] = thread;
          }
          
          set({ threads: threadsMap });
          console.log(`[CatchballStore] Loaded ${data.length} threads from Supabase`);
        }
      } catch (err) {
        console.error('[CatchballStore] Fetch threads exception:', err);
      }
    },
    

      // Get proposal
      getProposal: (id: string): CatchballProposal | undefined => {
        return get().proposals[id];
      },
      
      // Get proposals for cycle
      getProposalsForCycle: (cycleId: string): CatchballProposal[] => {
        return Object.values(get().proposals).filter(p => p.cycleId === cycleId);
      },
      
      // Get proposals for item
      getProposalsForItem: (itemId: string): CatchballProposal[] => {
        return Object.values(get().proposals).filter(p => p.itemId === itemId);
      },
      
      // Get loop-level proposals for a loop
      getLoopProposals: (loopId: string): CatchballProposal[] => {
        return Object.values(get().proposals).filter(
          p => p.loopId === loopId && p.proposalType === 'loop'
        );
      },
      
      // Get item-level proposals for a loop
      getItemProposals: (loopId: string): CatchballProposal[] => {
        return Object.values(get().proposals).filter(
          p => p.loopId === loopId && p.proposalType === 'item'
        );
      },
      
      // Get proposals by filter
      getProposalsByFilter: (filters: CatchballFilters): CatchballProposal[] => {
        let results = Object.values(get().proposals);
        
        if (filters.cycleId) {
          results = results.filter(p => p.cycleId === filters.cycleId);
        }
        
        if (filters.itemId) {
          results = results.filter(p => p.itemId === filters.itemId);
        }
        
        if (filters.direction) {
          results = results.filter(p => p.direction === filters.direction);
        }
        
        if (filters.status && filters.status.length > 0) {
          results = results.filter(p => filters.status!.includes(p.status));
        }
        
        if (filters.proposedBy) {
          results = results.filter(p => p.proposedBy === filters.proposedBy);
        }
        
        if (filters.targetOrgUnit) {
          results = results.filter(p => p.targetOrgUnit === filters.targetOrgUnit);
        }
        
        if (filters.isOverdue !== undefined) {
          results = results.filter(p => isProposalOverdue(p) === filters.isOverdue);
        }
        
        if (filters.threadId) {
          results = results.filter(p => p.threadId === filters.threadId);
        }
        
        return results;
      },
      
      // Get pending proposals
      getPendingProposals: (cycleId: string): CatchballProposal[] => {
        return get().getProposalsByFilter({ cycleId, status: ['pending'] });
      },
      
      // Get pending actions for user
      getPendingActions: (userId: string, cycleId?: string): PendingCatchballAction[] => {
        // Awaiting a decision exactly as the server's decision gate reads it
        // (763: pending / proposed / acknowledged). Stored packets are
        // 'proposed'; a 'pending'-only filter never matched a real row.
        let proposals = Object.values(get().proposals).filter(p =>
          canDecideProposal(p.status) &&
          p.targetRecipients?.includes(userId)
        );
        
        if (cycleId) {
          proposals = proposals.filter(p => p.cycleId === cycleId);
        }
        
        return proposals.map(p => ({
          proposalId: p.id,
          itemId: p.itemId ?? '',
          itemTitle: p.itemTitle || '',
          proposedBy: p.proposedBy,
          proposedByName: p.proposedByName,
          proposedAt: p.proposedAt,
          direction: p.direction,
          proposal: {
            rank: p.proposedRank,
            cutLine: p.proposedCutLine,
            action: p.proposedAction,
          },
          rationale: p.rationale,
          responseDeadline: p.responseDeadline,
          isOverdue: isProposalOverdue(p),
          daysPending: calculateDaysPending(p),
        }));
      },
      
      // Get counters for proposal
      getCountersForProposal: (proposalId: string): CatchballCounter[] => {
        return Object.values(get().counters).filter(c => c.proposalId === proposalId);
      },
      
      // Get thread
      getThread: (threadId: string): CatchballThread | undefined => {
        return get().threads[threadId];
      },
      
      // Get threads for cycle
      getThreadsForCycle: (cycleId: string): CatchballThread[] => {
        return Object.values(get().threads).filter(t => t.cycleId === cycleId);
      },
      
      // Get stats
      getStats: (cycleId: string): CatchballStats => {
        const proposals = get().getProposalsForCycle(cycleId);
        const threads = get().getThreadsForCycle(cycleId);
        
        // Count by status
        const statusCounts: Record<CatchballProposalStatus, number> = {
          pending: 0,
          draft: 0,
          proposed: 0,
          returned: 0,
          accepted: 0,
          accepted_provisional: 0,
          countered: 0,
          rejected: 0,
          withdrawn: 0,
        };
        
        let downwardCount = 0;
        let upwardCount = 0;
        let overdueCount = 0;
        let totalResponseTimeHours = 0;
        let respondedCount = 0;
        
        const participatingOrgUnits = new Set<string>();
        const participatingUsers = new Set<string>();
        
        for (const p of proposals) {
          statusCounts[p.status]++;
          
          if (p.direction === 'down') downwardCount++;
          else upwardCount++;
          
          if (isProposalOverdue(p)) overdueCount++;
          
          if (p.respondedAt && p.proposedAt) {
            const hours = (p.respondedAt.getTime() - p.proposedAt.getTime()) / (1000 * 60 * 60);
            totalResponseTimeHours += hours;
            respondedCount++;
          }
          
          if (p.proposedByOrgUnit) participatingOrgUnits.add(p.proposedByOrgUnit);
          if (p.targetOrgUnit) participatingOrgUnits.add(p.targetOrgUnit);
          participatingUsers.add(p.proposedBy);
        }
        
        // Thread counts
        const activeThreads = threads.filter(t => t.status === 'active').length;
        const resolvedThreads = threads.filter(t => t.status === 'resolved').length;
        const stalledThreads = threads.filter(t => t.status === 'stalled').length;
        
        return {
          cycleId,
          totalProposals: proposals.length,
          pendingProposals: statusCounts.pending,
          acceptedProposals: statusCounts.accepted,
          counteredProposals: statusCounts.countered,
          rejectedProposals: statusCounts.rejected,
          downwardProposals: downwardCount,
          upwardProposals: upwardCount,
          activeThreads,
          resolvedThreads,
          stalledThreads,
          averageResponseTimeHours: respondedCount > 0 ? totalResponseTimeHours / respondedCount : 0,
          overdueProposals: overdueCount,
          participatingOrgUnits: participatingOrgUnits.size,
          participatingUsers: participatingUsers.size,
          resolutionRate: proposals.length > 0 
            ? ((statusCounts.accepted + statusCounts.rejected) / proposals.length) * 100 
            : 0,
        };
      },
      
      // Get config
      getConfig: (cycleId: string): CatchballConfig | undefined => {
        return get().configs[cycleId];
      },

      // ─── Proposal Formation (Brief B) ───────────────────────────────────────

      formProposal: async (params) => {
        // Competing is settled in the Track (canon 12 Rule 28, owner ruling
        // 2026-10-12): a result with two competing items both above the line
        // does not go up until one is chosen.
        const competing = await findCompetingChosenInSession(params.sessionId);
        if (competing) {
          set({ error: competing });
          return null;
        }
        // §2.3a: one server transaction (catchball_draft_proposal, mig 772).
        // Everything the old client did in six round trips — read the ranking,
        // pick the latest item pool and session report, short-circuit on a
        // draft the loop already carries, find the revision parent, insert the
        // proposal and point the loop at it — happens inside it, all or
        // nothing. The direct INSERT/UPDATE the client used are closed.
        const result = await draftCatchballProposal({
          loopId: params.loopId,
          mode: 'session',
          fields: { session_id: params.sessionId, track_id: params.trackId },
          actorName: params.actorName,
        });
        if (!result.ok) {
          console.warn('[CatchballStore] catchball_draft_proposal refused:', result.error);
          set({ error: result.error ?? 'The proposal could not be packaged.' });
          return null;
        }
        const proposalId = (result.data as { proposal_id?: string } | undefined)?.proposal_id;
        if (!proposalId) {
          set({ error: 'The proposal could not be packaged.' });
          return null;
        }
        set({ error: null });
        return { proposalId };
      },

      recordProposalSession: async (params) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return false;

        const { data, error } = await supabase.rpc('catchball_record_proposal_session', {
          p_proposal_id: params.proposalId,
          p_session_id: params.sessionId,
          p_actor_id: params.actorId,
        });
        if (error) {
          console.error('[CatchballStore] recordProposalSession failed:', error);
          return false;
        }
        const result = data as { success?: boolean; error?: string } | null;
        if (!result?.success) {
          console.error('[CatchballStore] recordProposalSession rejected:', result?.error);
          return false;
        }
        return true;
      },

      submitProposal: async (params) => {
        set({ error: null });

        // Guard: rationale must be non-empty (client-side pre-check before RPC)
        if (!params.rationale.trim()) {
          console.warn('[CatchballStore] Cannot submit proposal: rationale is required');
          set({ error: 'A rationale is required before submitting.' });
          return false;
        }

        // §2.3a: Authority transition — the one shared path to
        // catchball_submit_proposal (src/lib/catchball/proposalDecisions.ts).
        const result = await submitCatchballProposal({
          proposalId: params.proposalId,
          actorId: params.actorId,
          rationale: params.rationale,
          tradeoffs: params.tradeoffs ?? null,
        });
        if (!result.ok) {
          console.warn('[CatchballStore] catchball_submit_proposal refused:', result.error);
          set({ error: result.error ?? 'The proposal could not be submitted.' });
          return false;
        }

        // The evaluator side is told server-side now: 763's
        // trg_notify_catchball_proposal_submitted notifies the recipients and
        // the loop's requester on this same submission.

        // No optimistic update. The caller (ProposalFormationDrawer) triggers a
        // loop re-fetch via catchballLoopStore after success so the UI reflects
        // the authoritative 'proposed' state from the DB.
        return true;
      },

      // ─── Executive Resolution Actions (Brief C) ─────────────────────────────
      //
      // One decision path for every surface and every proposal kind (763,
      // owner ruling 2026-09-22): catchball_accept / catchball_reject /
      // catchball_return_for_revision via src/lib/catchball/proposalDecisions.ts.

      acceptProposal: async (params) => {
        set({ error: null });

        // Fully atomic RPC — ratification + loop transition + cycle report in
        // one server-side transaction.
        const result = await acceptCatchballProposal({
          proposalId: params.proposalId,
          rationale: params.rationale ?? null,
          ratificationAuthority: params.ratificationAuthority ?? null,
        });
        if (!result.ok) {
          console.warn('[CatchballStore] catchball_accept refused:', result.error);
          set({ error: result.error ?? 'The proposal could not be ratified.' });
          return false;
        }

        // Warn if cycle report write failed inside the RPC — ratification is still intact
        const report = result.data?.report_result as { success?: boolean; error?: string } | undefined;
        if (report && report.success === false) {
          console.warn(
            '[CatchballStore] Cycle report write failed inside RPC — ratification intact. Manual retry available via Cycle → Results tab.',
            report.error
          );
        }

        // 737's trg_notify_catchball_proposal_resolved already tells the
        // proposer directly on this same status write. This only pings the
        // session facilitators/participants it does not reach (brief-X §5).
        notifyProposalEvent(params.proposalId, params.actorId, 'accepted');

        return true;
      },

      acceptProposalWithConditions: async (params) => {
        if (!params.conditions.trim()) {
          set({ error: 'Conditions are required.' });
          return false;
        }
        set({ error: null });

        // Accept with conditions = provisional acceptance carrying the
        // conditions (canon 03 §2.3d "Accept (Provisional)").
        const result = await acceptCatchballProposal({
          proposalId: params.proposalId,
          provisional: true,
          conditions: params.conditions,
        });
        if (!result.ok) {
          console.warn('[CatchballStore] catchball_accept (provisional) refused:', result.error);
          set({ error: result.error ?? 'The proposal could not be accepted with conditions.' });
          return false;
        }

        // No client send here: 737's trg_notify_catchball_proposal_resolved
        // fires on this same status write ('accepted_provisional') and tells
        // the proposer directly — this path had no other recipient to notify.
        return true;
      },

      returnProposal: async (params) => {
        if (!params.revisionGuidance.trim()) {
          set({ error: 'Revision guidance is required.' });
          return false;
        }
        set({ error: null });

        const result = await returnCatchballProposal({
          proposalId: params.proposalId,
          revisionGuidance: params.revisionGuidance,
        });
        if (!result.ok) {
          console.warn('[CatchballStore] catchball_return_for_revision refused:', result.error);
          set({ error: result.error ?? 'The proposal could not be returned.' });
          return false;
        }

        // No client send here: 737's trg_notify_catchball_proposal_resolved
        // fires on this same status write ('returned') and tells the proposer
        // directly — this path had no other recipient to notify.
        return true;
      },

      rejectProposal: async (params) => {
        if (!params.rationale.trim()) {
          set({ error: 'A rationale is required.' });
          return false;
        }
        set({ error: null });

        const result = await rejectCatchballProposal({
          proposalId: params.proposalId,
          rationale: params.rationale,
        });
        if (!result.ok) {
          console.warn('[CatchballStore] catchball_reject refused:', result.error);
          set({ error: result.error ?? 'The proposal could not be rejected.' });
          return false;
        }

        // 737's trg_notify_catchball_proposal_resolved already tells the
        // proposer directly on this same status write. This only pings the
        // session facilitators/participants it does not reach (brief-X §5).
        notifyProposalEvent(params.proposalId, params.actorId, 'rejected');

        return true;
      },

      // ─── Loop Results read paths (Brief D) ──────────────────────────────────

      fetchLoopHistory: async (loopId: string): Promise<LoopResult[]> => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return [];
        const { data } = await supabase
          .from(LOOP_RESULTS_TABLE)
          .select('*')
          .eq('catchball_loop_id', loopId)
          .order('turn_number', { ascending: true });
        return (data as LoopResult[]) ?? [];
      },

      fetchCycleLoopResults: async (cycleId: string): Promise<LoopResult[]> => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return [];
        const { data } = await supabase
          .from(LOOP_RESULTS_TABLE)
          .select('*')
          .eq('cycle_id', cycleId)
          .order('submitted_at', { ascending: true });
        return (data as LoopResult[]) ?? [];
      },

      // Reset
      reset: () => set(initialState),
      
      // Loading
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
    })
);

// Selector hooks
// Each hook subscribes to the minimal raw store slice needed for reactivity,
// then derives the result with useMemo so the returned value has a stable
// reference between renders (prevents the infinite-loop caused by selectors
// that return new array/object literals on every call).

export const useCatchballProposal = (id: string) =>
  useCatchballStore(state => state.getProposal(id));

// The subscribed slice in each dep array below looks "unnecessary" to
// react-hooks/exhaustive-deps because the body reads through getState() rather
// than the subscribed value — but that slice IS the recompute signal. Dropping
// it freezes the derived result at its first value. Disabled deliberately, per
// the pattern note above.

export function useCatchballProposalsForCycle(cycleId: string) {
  const proposals = useCatchballStore(state => state.proposals);
  return useMemo(
    () => useCatchballStore.getState().getProposalsForCycle(cycleId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `proposals` is the recompute signal
    [proposals, cycleId],
  );
}

export function usePendingCatchballActions(userId: string, cycleId?: string) {
  const proposals = useCatchballStore(state => state.proposals);
  return useMemo(
    () => useCatchballStore.getState().getPendingActions(userId, cycleId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `proposals` is the recompute signal
    [proposals, userId, cycleId],
  );
}

export function useCatchballStats(cycleId: string) {
  const proposals = useCatchballStore(state => state.proposals);
  const threads = useCatchballStore(state => state.threads);
  return useMemo(
    () => useCatchballStore.getState().getStats(cycleId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `proposals`/`threads` are the recompute signals
    [proposals, threads, cycleId],
  );
}

export function useCatchballThreads(cycleId: string) {
  const threads = useCatchballStore(state => state.threads);
  return useMemo(
    () => useCatchballStore.getState().getThreadsForCycle(cycleId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `threads` is the recompute signal
    [threads, cycleId],
  );
}
